<script setup lang="ts">
import { watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { OrganizerRefund } from '@/types/refunds'
import { useRegistrationRefund, REASON_MAX_LENGTH } from '@/composables/useRegistrationRefund'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    registrationId: number
    prefillAmount?: string | null
  }>(),
  {
    prefillAmount: null,
  },
)

const emit = defineEmits<{
  close: []
  issued: [payload: { refund: OrganizerRefund; unconfirmed: boolean }]
}>()

const { t } = useI18n()

const {
  preview,
  amount,
  reason,
  loadingPreview,
  previewError,
  submitting,
  fieldErrors,
  formError,
  formErrorKey,
  remainingAfter,
  amountError,
  canSubmit,
  fetchPreview,
  applyDefaultAmount,
  submit,
  reset,
} = useRegistrationRefund()

const blockedReason = computed(() => preview.value?.blocked_reason ?? null)

const formErrorText = computed<string | null>(() => {
  if (formError.value !== null) return formError.value
  return formErrorKey.value !== null ? t(formErrorKey.value) : null
})

const amountErrorText = computed<string | null>(() => {
  if (fieldErrors.value.amount !== undefined) return fieldErrors.value.amount
  // Do not nag about an empty field before the user has typed anything.
  if (amount.value === '' || amountError.value === null) return null
  return t(amountError.value.key, amountError.value.params ?? {})
})

async function load(): Promise<void> {
  reset()
  await fetchPreview(props.registrationId)
  applyDefaultAmount(props.prefillAmount)
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) void load()
  },
  { immediate: true },
)

function formatCurrency(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(
    parseFloat(value),
  )
}

const submitAmountLabel = computed<string>(() =>
  formatCurrency(amountError.value === null ? amount.value.replace(',', '.') : '0'),
)

function handleClose(): void {
  if (submitting.value) return
  emit('close')
}

async function handleSubmit(): Promise<void> {
  const result = await submit(props.registrationId)
  if (result !== null) {
    emit('issued', result)
  }
}
</script>

<template>
  <SlideOver
    :open="open"
    :title="t('participants.refunds.panel_title')"
    :saving="submitting"
    @close="handleClose"
  >
    <!-- Loading -->
    <div v-if="loadingPreview" class="flex justify-center py-10">
      <AppSpinner />
    </div>

    <!-- Preview error -->
    <div
      v-else-if="previewError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      role="alert"
      data-testid="refund-preview-error"
    >
      <p>{{ t('common.error_generic') }}</p>
      <button
        type="button"
        class="mt-2 text-sm font-semibold text-red-300 hover:text-red-200 underline"
        data-testid="refund-preview-retry"
        @click="load"
      >
        {{ t('participants.refunds.retry') }}
      </button>
    </div>

    <template v-else-if="preview">
      <!-- Summary -->
      <dl
        class="rounded-xl border border-slate-700 bg-slate-900/40 p-4 text-sm space-y-2 mb-5"
        data-testid="refund-summary"
      >
        <div class="flex justify-between">
          <dt class="text-slate-400">{{ t('participants.refunds.total_paid') }}</dt>
          <dd class="text-white" data-testid="refund-summary-paid">{{ formatCurrency(preview.paid_amount) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-slate-400">{{ t('participants.refunds.already_refunded') }}</dt>
          <dd class="text-white" data-testid="refund-summary-refunded">{{ formatCurrency(preview.refunded_amount) }}</dd>
        </div>
        <div v-if="parseFloat(preview.refund_pending_amount) > 0" class="flex justify-between">
          <dt class="text-slate-400">{{ t('participants.refunds.being_processed') }}</dt>
          <dd class="text-white" data-testid="refund-summary-pending">{{ formatCurrency(preview.refund_pending_amount) }}</dd>
        </div>
        <div class="flex justify-between border-t border-slate-700 pt-2">
          <dt class="text-white font-semibold">{{ t('participants.refunds.can_still_refund') }}</dt>
          <dd class="text-white font-semibold" data-testid="refund-summary-remaining">{{ formatCurrency(preview.refundable_remaining) }}</dd>
        </div>
      </dl>

      <!-- Blocked -->
      <div
        v-if="blockedReason !== null"
        class="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-300"
        role="status"
        data-testid="refund-blocked-notice"
      >
        {{ t(`participants.refunds.blocked.${blockedReason}`) }}
      </div>

      <!-- Form -->
      <form v-else id="issue-refund-form" class="space-y-5" novalidate @submit.prevent="handleSubmit">
        <div>
          <label for="refund-amount" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('participants.refunds.amount_label') }}
          </label>
          <input
            id="refund-amount"
            v-model="amount"
            type="text"
            inputmode="decimal"
            autocomplete="off"
            :disabled="submitting"
            class="w-full rounded-lg bg-slate-900 border text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
            :class="amountErrorText ? 'border-red-500' : 'border-slate-600'"
            data-testid="refund-amount-input"
          />
          <p v-if="amountErrorText" class="mt-1 text-sm text-red-400" data-testid="refund-amount-error">
            {{ amountErrorText }}
          </p>
          <p class="mt-1 text-xs text-slate-400" data-testid="refund-remaining-after">
            {{ t('participants.refunds.remaining_after', { amount: formatCurrency(remainingAfter) }) }}
          </p>
        </div>

        <div>
          <label for="refund-reason" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('participants.refunds.reason_label') }}
          </label>
          <textarea
            id="refund-reason"
            v-model="reason"
            rows="3"
            :maxlength="REASON_MAX_LENGTH"
            :disabled="submitting"
            class="w-full rounded-lg bg-slate-900 border text-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
            :class="fieldErrors.reason ? 'border-red-500' : 'border-slate-600'"
            data-testid="refund-reason-input"
          />
          <div class="mt-1 flex items-start justify-between gap-3">
            <p class="text-xs text-slate-400" data-testid="refund-reason-hint">
              {{ t('participants.refunds.reason_hint') }}
            </p>
            <p class="text-xs text-slate-500 shrink-0" data-testid="refund-reason-counter">
              {{ t('participants.refunds.reason_counter', { count: reason.length, max: REASON_MAX_LENGTH }) }}
            </p>
          </div>
          <p v-if="fieldErrors.reason" class="mt-1 text-sm text-red-400" data-testid="refund-reason-error">
            {{ fieldErrors.reason }}
          </p>
        </div>

        <p class="text-xs text-slate-400">{{ t('participants.refunds.info_email_and_fee') }}</p>
      </form>

      <!-- Provider / form level error (409, 422 refund_rejected, 502, 403) -->
      <div
        v-if="formErrorText"
        class="mt-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        role="alert"
        data-testid="refund-error"
      >
        {{ formErrorText }}
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="submitting"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          data-testid="refund-cancel"
          @click="handleClose"
        >
          {{ blockedReason !== null ? t('common.close') : t('common.cancel') }}
        </button>
        <button
          v-if="blockedReason === null && preview !== null"
          type="submit"
          form="issue-refund-form"
          :disabled="!canSubmit"
          class="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2"
          data-testid="refund-submit"
        >
          <AppSpinner v-if="submitting" size="button" />
          <template v-if="submitting">{{ t('common.loading') }}</template>
          <template v-else>{{ t('participants.refunds.submit', { amount: submitAmountLabel }) }}</template>
        </button>
      </div>
    </template>
  </SlideOver>
</template>
